import * as React from 'react'
import { useEffect, useState } from 'react'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import Checkbox from '@/modules/drivers/shared/Checkbox'
import {
  useMutationSplitServiceCompany,
  useQueryServiceCompanySplitPreview,
} from '@/modules/shared/query-client/useServiceAccountAdmin'
import { Th, Td, extractCode } from '@/modules/shared/components/serviceAccount/ServiceAccountRoster'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

const KNOWN_ERRORS = [
  'NOT_SERVICE_COMPANY',
  'SERVICE_COMPANY_HAS_NO_DATABASE',
  'SPLIT_REQUIRES_MANUAL_BILLING',
  'COMPANY_HAS_TENANT',
  'COMPANY_HAS_CHILDREN',
  'COMPANY_HAS_NO_DATABASE',
  'SPLIT_UNKNOWN_USER',
  'NOT_AUTHORIZED',
]

const splitErrorKey = (code: string | null | undefined): string =>
  `serviceAccountSplit.error-${code && KNOWN_ERRORS.includes(code) ? code : 'DEFAULT'}`

// convert: an ordinary company gets a new service account created above it (same command,
// nothing to move but the company itself).
export const SplitServiceCompanyModal: React.FC<{
  account: { id: string; companyName: string }
  convert?: boolean
  onClose: () => void
  onSplit: (newAccountId: string) => void
}> = ({ account, convert = false, onClose, onSplit }) => {
  const { t } = useLocalize()
  const { data, isLoading, error } = useQueryServiceCompanySplitPreview(account.id)
  const split = useMutationSplitServiceCompany()
  const [wholeAccount, setWholeAccount] = useState<Set<number>>(new Set())
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    if (!data) return
    setWholeAccount(new Set(data.users.filter((u) => u.suggestWholeAccount).map((u) => u.userId)))
  }, [data])

  const toggle = (userId: number) =>
    setWholeAccount((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })

  const onConfirm = () => {
    setActionError(null)
    split.mutate(
      { accountId: account.id, wholeAccountUserIds: Array.from(wholeAccount) },
      {
        onSuccess: (res) => onSplit(res.serviceAccountId),
        onError: (e) => setActionError(t(splitErrorKey(extractCode(e)))),
      },
    )
  }

  const key = (name: string) => `serviceAccountSplit.${name}${convert ? '-convert' : ''}`
  const blocked = data?.blockedReason ?? null
  const canConfirm = !!data && !blocked && !split.isPending

  return (
    <ModalLayout customZIndex={10000}>
      <div className="tw-flex tw-max-h-[90vh] tw-w-[760px] tw-max-w-[calc(100vw-32px)] tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-xl">
        <div className="tw-border-b tw-border-gray-200 tw-p-4">
          <h2 className="tw-text-lg tw-font-semibold tw-text-gray-800">
            {t(key('title'))}
          </h2>
          <p className="tw-text-sm tw-text-gray-600">{account.companyName}</p>
        </div>

        <div className="tw-flex-1 tw-overflow-y-auto tw-p-4 tw-text-sm tw-text-gray-700">
          {isLoading && <div className="tw-py-6 tw-text-center tw-text-gray-500">{t('serviceAccountSplit.loading')}</div>}
          {error && (
            <div className="tw-rounded tw-bg-red-100 tw-px-3 tw-py-2 tw-text-red-800">
              {t(splitErrorKey(extractCode(error)))}
            </div>
          )}

          {data && (
            <>
              <ul className="tw-mb-4 tw-list-disc tw-space-y-1 tw-pl-5">
                <li>
                  {t('serviceAccountSplit.step-new-account', { nip: data.nip ?? '—' })}
                </li>
                <li>
                  {t('serviceAccountSplit.step-former-company', { company: data.companyName })}
                </li>
                {!convert && (
                  <li>{t('serviceAccountSplit.step-children', { count: data.childCount })}</li>
                )}
                <li>{t(key('step-access'), { company: data.companyName })}</li>
              </ul>

              {blocked && (
                <div className="tw-mb-3 tw-rounded tw-bg-red-100 tw-px-3 tw-py-2 tw-text-red-800">
                  {t(splitErrorKey(blocked))}
                </div>
              )}

              {!convert && data.groupRoleUserCount > 0 && (
                <div className="tw-mb-3 tw-rounded tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-amber-800">
                  {t('serviceAccountSplit.group-roles-warning', {
                    count: data.groupRoleUserCount,
                    company: data.companyName,
                  })}
                </div>
              )}

              <div className="tw-overflow-x-auto tw-rounded tw-border tw-border-gray-200">
                <table className="tw-w-full tw-text-sm">
                  <thead className="tw-bg-gray-50 tw-text-xs tw-uppercase tw-text-gray-600">
                    <tr>
                      <Th>{t('serviceAccountSplit.column-whole-account')}</Th>
                      <Th>{t('serviceAccountSplit.column-user')}</Th>
                      <Th>{t('serviceAccountSplit.column-current-access')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.length === 0 && (
                      <tr>
                        <td colSpan={3} className="tw-py-6 tw-text-center tw-text-gray-500">
                          {t(key('no-users'))}
                        </td>
                      </tr>
                    )}
                    {data.users.map((u) => {
                      const name = [u.firstName, u.surname].filter(Boolean).join(' ')
                      return (
                        <tr key={u.userId} className="tw-border-t tw-border-gray-100">
                          <Td>
                            <Checkbox
                              checked={wholeAccount.has(u.userId)}
                              onChange={() => toggle(u.userId)}
                            />
                          </Td>
                          <Td>
                            {name && <div className="tw-font-medium tw-text-gray-900">{name}</div>}
                            <div className="tw-text-xs tw-text-gray-500">{u.login}</div>
                          </Td>
                          <Td>
                            <div className="tw-flex tw-flex-wrap tw-gap-1">
                              {u.roles.map((r) => (
                                <span
                                  key={r}
                                  className="tw-rounded tw-bg-blue-50 tw-px-2 tw-py-0.5 tw-text-xs tw-text-blue-800"
                                >
                                  {r}
                                </span>
                              ))}
                              {u.matchesAccountEmail && (
                                <span className="tw-rounded tw-bg-emerald-50 tw-px-2 tw-py-0.5 tw-text-xs tw-text-emerald-800">
                                  {t('serviceAccountSplit.badge-company-email')}
                                </span>
                              )}
                              {u.isRosterMember && (
                                <span className="tw-rounded tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-text-xs tw-text-gray-700">
                                  {t(
                                    u.isRosterOwner
                                      ? 'serviceAccountSplit.badge-roster-owner'
                                      : 'serviceAccountSplit.badge-roster-member',
                                  )}
                                </span>
                              )}
                            </div>
                          </Td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {wholeAccount.size === 0 && (
                <div className="tw-mt-2 tw-rounded tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-amber-800">
                  {t('serviceAccountSplit.no-owner-warning')}
                </div>
              )}
              <p className="tw-mt-2 tw-text-xs tw-text-gray-500">
                {t('serviceAccountSplit.footnote', { company: data.companyName })}
              </p>
            </>
          )}

          {actionError && (
            <div className="tw-mt-3 tw-rounded tw-bg-red-100 tw-px-3 tw-py-2 tw-text-red-800">
              {actionError}
            </div>
          )}
        </div>

        <div className="tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-gray-200 tw-p-4">
          <button
            onClick={onClose}
            disabled={split.isPending}
            className="tw-cursor-pointer tw-rounded tw-border tw-border-gray-300 tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 disabled:tw-opacity-50"
          >
            {t('serviceAccountSplit.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm}
            className="tw-cursor-pointer tw-rounded tw-bg-blue-600 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-blue-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          >
            {split.isPending ? t(key('submitting')) : t(key('submit'))}
          </button>
        </div>
      </div>
    </ModalLayout>
  )
}
